<script>
import { mapGetters } from 'vuex'

import appConfig from '@src/app.config.json'
import Layout from '@layouts/main.vue'
import SearchInput from '@components/navigation/search/search-input.vue'
import ActionButton from '@components/common/action-button.vue'
import BenefitsSection from '@components/homepage/benefits-tabs.vue'
import OffersSection from '@components/homepage/offers-section.vue'
import HowToUseSection from '@components/homepage/how-to-use-section.vue'
import NewsSection from '@components/homepage/news-section.vue'
import Slider from '@components/common/sliders/attractions-slider.vue'
import CalculatorSection from '@components/common/calculator-section.vue'
import ReviewsSlider from '@components/common/sliders/reviews-slider.vue'
import StarsRating from '@components/stars-rating.vue'
import FeedbackModal from '@components/common/feedback-modal.vue'
import { SwiperSlide, Swiper } from 'vue-awesome-swiper'

import Translatable from '@src/mixins/translatable.js'
import SanitizerMixin from '@src/mixins/sanitizer-mixin.js'
import ImageMixin from '@src/mixins/imageMixin.js'
import Resizable from '@src/mixins/resizable.js'
import AuthorLabel from '@src/mixins/author.js'

import PhoneImage from '@assets/images/mobile.png'
import PragueCardImage from '@assets/images/prague-card-image.png'
import ListBullet from '@assets/icons/list-bullet.svg'

export default {
  page: {
    title: 'Prague CoolPass / Prague Card: All-inclusive City Pass',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    CalculatorSection,
    Slider,
    Layout,
    ActionButton,
    SearchInput,
    BenefitsSection,
    OffersSection,
    NewsSection,
    ReviewsSlider,
    StarsRating,
    HowToUseSection,
    FeedbackModal,
    SwiperSlide,
    Swiper,
  },
  mixins: [Translatable, SanitizerMixin, ImageMixin, Resizable, AuthorLabel],
  data() {
    return {
      backgrounds: {
        phoneBackground: {
          backgroundImage: `url(${PhoneImage})`,
        },
        pragueCardBackground: {
          backgroundImage: `url(${PragueCardImage})`,
        },
      },
      swiperOptions: {
        autoplay: {
          delay: 5000,
        },
        speed: 1300,
        allowTouchMove: true,
        loop: true,
        slidesPerView: 1,
        slidesPerGroup: 1,
        pagination: {
          el: '.header-pagination',
          clickable: true,
          bulletClass: 'bullet',
          bulletActiveClass: 'bullet-active',
        },
      },
      showFeedback: false,
      isFirstRendered: false,
    }
  },
  computed: {
    ...mapGetters('home', ['redirectValue', 'pageData']),
    ...mapGetters('attractions', [
      'topAttractions',
      'attractions',
      'aisLoading',
    ]),
    ...mapGetters('reviews', ['averageRating', 'fifteenRandomReviews']),
    getListStyleBackground() {
      return {
        listStyleImage: `url(${ListBullet})`,
      }
    },
    pageImg() {
      if (this.pageData.mainImage) {
        return this.desktopScreen
          ? this.pageData.mainImage.web_image
          : this.pageData.mainImage.app_image
      }
      return ''
    },
  },
  methods: {
    redirectToAttraction() {
      if (this.redirectValue) {
        this.$router.push({
          name: 'attraction',
          params: { slug: this.redirectValue },
        })
        this.$store.commit('home/SET_REDIRECT_VALUE', '')
        this.$refs.search.$children[0].clear()
      } else {
        this.$store.commit('home/SET_BUTTON_EVENT', true)
      }
    },
    toggle(el) {
      this[el] = !this[el]
      document.body.style.overflow = this.showFeedback ? 'hidden' : ''
    },
    pageContentU(prop) {
      if (this.pageData.content) {
        return this.pageData.content[this.currentLanguage][
          prop
        ].descriptions.map((_, i) => {
          return this.pageData.content[this.currentLanguage][prop].descriptions[
            i
          ]
            ? this.pageData.content[this.currentLanguage][prop].descriptions[i]
            : this.pageData.content.en[prop].descriptions[i]
        })
      }
      return []
    },
  },
  created() {
    this.$store.dispatch('attractions/fetchTopAttractions')
  },
  updated() {
    if (this.pageData.content && !this.isFirstRendered) {
      if (this.pageData.is_modal_visible) {
        this.toggle('showFeedback')
      }
      this.isFirstRendered = !this.isFirstRendered
    }
  },
}
</script>

<template>
  <div>
    <div v-if="aisLoading"></div>
    <Layout v-else-if="topAttractions.length">
      <FeedbackModal
        v-if="showFeedback"
        :text="getTranslatedContent('modal_description', pageData.content)"
        @toggle="toggle"
      />
      <section class="header-slider">
        <Swiper v-if="pageImg.length > 1" :options="swiperOptions">
          <SwiperSlide v-for="(image, index) in pageImg" :key="index">
            <div
              class="slide"
              :style="
                desktopScreen ? getBackground(image) : getBackgroundSmall(image)
              "
            ></div>
          </SwiperSlide>
          <div slot="pagination" class="header-pagination"></div>
        </Swiper>
        <div
          v-else
          class="slide"
          :style="
            desktopScreen
              ? getBackground(pageImg[0])
              : getBackgroundSmall(pageImg[0])
          "
        ></div>
      </section>
      <section class="justify-center">
        <div class="container header-content">
          <div class="title-block">
            <h1 class="header-title"
                v-html="getTranslatedContent('title', pageData.content)">
                <!-- {{
              getTranslatedContent('title', pageData.content)
                }} -->
            </h1>
            <h3
              class="header-subtitle"
              v-html="getTranslatedContent('subtitle', pageData.content)"
            ></h3>
          </div>
          <div v-if="desktopScreen" class="form-container container">
            <SearchInput ref="search" :attractions="attractions" />
            <ActionButton
              :class="'show-attractions-btn'"
              :caption="getTranslatedContent('HOME_lets_go_button')"
              :action="redirectToAttraction"
            />
          </div>
        </div>
      </section>
      <section class="underbar justify-center">
        <p class="author container" v-html="authorLabel(pageData)"></p>
        <p
          class="underbar-text container"
          v-html="getTranslatedContent('header_banner', pageData.content)"
        >
        </p>
      </section>
      <section v-if="!desktopScreen" class="form-container container">
        <SearchInput ref="search" :attractions="attractions" />
        <ActionButton
          :class="'show-attractions-btn'"
          :caption="getTranslatedContent('HOME_lets_go_button')"
          :action="redirectToAttraction"
        />
      </section>
      <section id="top-attractions" class="justify-center">
        <div class="container">
          <h3
            class="top-attractions-title"
            v-html="getTranslatedContent('HOME_top_attractions_title')"
          ></h3>
        </div>
      </section>
      <section class="justify-center top-attractions-carousel">
        <Slider :attractions="topAttractions" :isntPaginated="true" />
      </section>
      <section id="coolpass-benefits" class="benefits-section justify-center">
        <div class="container">
          <h3
            class="benefits-title"
            v-html="getTranslatedContent('HOME_benefits_title')"
          ></h3>
        </div>
        <div class="container benefits-content-container">
          <div class="news-tabs-container">
            <BenefitsSection />
          </div>
          <div class="phone-content">
            <div
              class="mobile-phone"
              :style="backgrounds.phoneBackground"
            ></div>
            <div
              class="prague-card-image"
              :style="backgrounds.pragueCardBackground"
            ></div>
          </div>
        </div>
      </section>
      <section class="offers-section justify-center">
        <div class="container">
          <h3
            class="offers-section-title"
            v-html="getTranslatedContent('HOME_offers_title')"
          >
          </h3>
          <OffersSection />
        </div>
      </section>
      <section id="how-to-use" class="how-to-use justify-center">
        <div class="container">
          <h3
            class="how-to-use-title"
            v-html="getTranslatedContent('HOME_how_to_use_title')"
          >
          </h3>
          <HowToUseSection :list="pageContentU('how_to_use')" />
        </div>
      </section>
      <section class="news-section justify-center">
        <div class="container">
          <h3
            class="news-section-title"
            v-html="getTranslatedContent('HOME_news_title')"
          >
          </h3>
          <NewsSection />
        </div>
      </section>
      <section class="calculator-section justify-center">
        <div class="container">
          <h3
            class="calculator-section-title"
            v-html="getTranslatedContent('BUY_COOLPASS_PRAGUE_CARD')"
          >
          </h3>
          <CalculatorSection />
          <div class="tips">
            <div class="first-tip-block">
              <ul :style="getListStyleBackground">
                <li>{{ getTranslatedContent('CALCULATOR_card_validity') }}</li>
                <li>{{
                  getTranslatedContent('CALCULATOR_child_card_validity_tip')
                }}</li>
              </ul>
            </div>
            <div class="second-tip-block">
              <ul :style="getListStyleBackground">
                <li>{{
                  getTranslatedContent('CALCULATOR_student_id_info')
                }}</li>
              </ul>
            </div>
            <div class="third-tip-block">
              <p>{{ getTranslatedContent('ADULT_AGE') }}</p>
              <p>{{ getTranslatedContent('STUDENT_AGE') }}</p>
              <p>{{ getTranslatedContent('CHILD_AGE') }}</p>
            </div>
          </div>
        </div>
      </section>
      <section class="reviews-section justify-center">
        <div class="container">
          <div class="reviews-section-header">
            <h3
              class="reviews-title"
              v-html="getTranslatedContent('REVIEWS_what_do_customers_say')"
            >
            </h3>
            <div class="rating-wrapper">
              <div class="average-review-rating">
                <div class="average-rating">
                  {{ averageRating && averageRating.toFixed(1) }}
                </div>
              </div>
              <StarsRating
                :rating="averageRating && averageRating.toFixed(0)"
              />
            </div>
          </div>
        </div>
      </section>
      <section>
        <ReviewsSlider :reviews="fifteenRandomReviews" />
      </section>
    </Layout>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.attraction-search {
  width: 274px;
  height: 37px;
  margin-top: 30px;
  background: #ffffff;
  border-radius: 5px;
}

.header-text-container {
  margin-top: 267px;
  margin-bottom: 30px;
  width: 1026px;
  height: 135px;
  // background: rgba(37, 44, 62, 0.36);
}

.form-container {
  display: flex;
  flex-direction: row;
  margin-left: 26px;
  margin-top: 27px;
}

.author {
  position: absolute;
  margin-top: -25px;
  text-align: right;
  font-family: Open Sans, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 16px;
  color: $light-gray;
  opacity: 0.9;
}

h1.main-text {
  font-family: Arial, sans-serif;
  font-style: normal;
  font-weight: bold;
  font-size: 48px;
  line-height: 130%;
  color: #ffffff;
  text-transform: uppercase;
  padding-top: 11px;
  padding-left: 22px;
}

h2.secondary-text {
  font-family: Arial, sans-serif;
  font-style: normal;
  font-weight: bold;
  font-size: 26px;
  line-height: 130%;
  color: #ffffff;
  margin-left: 25px;
  margin-top: 8px;
}

.category-picker {
  margin-left: 103px;
}

.area-picker {
  margin-left: 13px;
  margin-right: 21px;
}

.underbar {
  width: 100%;
  height: 50px;
  background: $orange;
  color: $white;
}

.underbar-text {
  align-self: center;
  font-family: Open Sans, sans-serif;
  font-style: normal;
  font-weight: bold;
  font-size: 14px;
  letter-spacing: 0.5px;
  // text-align: center;
  line-height: 130%;
}

.show-attractions-btn {
  margin-left: 21px;
  min-width: 70px;
  width: auto;
  padding: 0 20px;
  height: 37px;
}

h3 {
  font-family: 'Open Sans Bold', sans-serif; //temp-text
  font-weight: bold;
  // font-family: "Open Sans", sans-serif;
  // font-weight: normal;
  font-style: normal;
  font-size: 22px;
  line-height: 130%;
  text-transform: uppercase;
  color: $dark-blue;
}

.how-to-use-title {
  margin-top: 50px;
}

.top-attractions-title {
  margin-top: 40px;
}

.top-attractions-carousel,
.benefits-section {
  margin-top: 40px;
}

.benefits-section {
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  align-items: center;
}

.phone-content {
  margin-left: 67px;
  display: flex;
  flex-direction: row;
}

.mobile-phone {
  margin-top: 52px;
  background-repeat: no-repeat;
  background-size: cover;
  height: 390px;
  width: 195px;
  z-index: 2;
}

.prague-card-image {
  background-repeat: no-repeat;
  background-size: cover;
  margin-top: 188px;
  margin-left: 130.73px;
  width: 263px;
  height: 176px;
  position: absolute;
  border-radius: 10px;
}

.benefits-content-container {
  display: flex;
  flex-direction: row;
  margin-top: 30px;
}

.offers-section-title {
  margin-top: 50px;
  margin-bottom: 30px;
}

.calculator-section-title,
.news-section-title {
  margin-bottom: 30px;
  margin-top: 50px;
}

.tips {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 20px;
  height: auto;
}

.tips ul {
  list-style: disc outside;
  width: 354px;
}

.tips ul li {
  font-family: Open Sans, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 16px;
  color: $black;
  // opacity: 0.8;
  margin-bottom: 11px;
  padding-left: 10px;
}

.first-tip-block,
.second-tip-block {
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;
}

.third-tip-block {
  margin-top: 25px;
}

.third-tip-block p {
  font-family: Open Sans Bold, sans-serif;
  font-weight: bold;
  font-size: 14px;
  line-height: 18px;
  color: $black;
  // opacity: 0.8;
  margin-bottom: 10px;
}

.third-tip-block p:last-child {
  margin-bottom: 0;
}

.reviews-section {
  margin-top: 51px;
}

.reviews-section-header {
  display: flex;
  align-items: center;
  flex-direction: row;
  margin-bottom: 30px;
}

.rating-wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-left: auto;
}

.average-review-rating {
  margin-left: auto;
  margin-right: 12px;
  font-family: Open Sans Bold, sans-serif;
  font-weight: bold;
  font-size: 22px;
  line-height: 29px;
  color: $dark-blue;
}

.header-slider {
  min-height: 550px;
}

::v-deep .header-pagination {
  @extend .pagination;
  position: absolute;
  bottom: 15px;
  transform: scale(1.4);
  z-index: 1;

  & .bullet {
    background: $white;
    opacity: 0.8;
  }

  & .bullet-active {
    background: $orange;
    opacity: 1;
  }
}

.bullet {
  background: $white;
  opacity: 0.8;
}

::v-deep .swiper-wrapper,
.swiper-container {
  z-index: auto;
}

.slide {
  height: 550px;
  background-repeat: no-repeat;
  background-position: center;
  z-index: -1;
}

.header-content {
  position: absolute;
  bottom: 327px;
}

.title-block {
  margin-top: 53px;
  width: 1026px;
  // height: 135px;
  // background: rgba(37, 44, 62, 0.36);
  padding: 9px 0 35px 22px;
  box-sizing: border-box;

  .header-title {
    font-family: Arial, sans-serif;
    font-style: normal;
    font-weight: bold;
    font-size: 48px;
    line-height: 130%;
    color: $white;
    text-transform: uppercase;
  }

  .header-subtitle {
    font-family: Arial, sans-serif;
    font-style: normal;
    font-weight: bold;
    font-size: 26px;
    line-height: 130%;
    color: $white;
    text-transform: none;
    margin-top: 8px;
    margin-left: 3px;
  }
}

// mobile layout

@media only screen and (min-width: $mobile-screen-width) and (max-width: $screen-width-xs) {
  h3 {
    font-family: Open Sans Bold, sans-serif;
    font-style: normal;
    font-weight: bold;
    font-size: 18px;
    line-height: 23px;
    text-transform: uppercase;
    color: $dark-blue;
  }

  .header-caption {
    display: none;
  }

  .header-text-container {
    margin-top: 70px;
    margin-bottom: 107px;
    width: auto;
    height: 125px;
  }

  h1.main-text {
    padding-top: 34px !important;
    font-size: 26px !important;
    line-height: 34px;
    margin-left: 8px;
  }

  h2.secondary-text {
    font-size: 16px !important;
    margin-left: 8px;
  }

  .header-content {
    height: 244px;

    :first-child {
      padding: 0;
    }
  }

  .form-container {
    margin-top: 20px;
    margin-left: 0;
    position: relative;
    flex-direction: column;
    align-items: center;
  }

  .underbar {
    min-height: 38px;
    height: auto;
  }

  .underbar-text {
    margin: 8px 0;
  }

  .top-attractions-title {
    margin-top: 40px;
  }

  .show-attractions-btn {
    margin: 12px 0 0;
    width: 100%;
    height: 37px;
  }

  .benefits-content-container {
    flex-direction: column-reverse;
  }

  .phone-content {
    margin: 0 0 11px;
    justify-content: flex-start;
    position: relative;

    .mobile-phone {
      margin-top: 0;
    }
  }

  .mobile-phone {
    margin-top: 40px;
    margin-left: 40px;
    background-repeat: no-repeat;
    background-size: cover;
    height: 246px;
    width: 118.5px;
    z-index: 2;
  }

  .news-tabs-container {
    box-sizing: border-box;
  }

  .prague-card-image {
    background-repeat: no-repeat;
    background-size: cover;
    margin-top: 106px;
    margin-left: 119.44px;
    width: 172px;
    height: 115px;
    position: absolute;
  }

  .tips {
    grid-template-columns: repeat(1, 1fr);
  }

  .tips ul {
    width: 100%;
  }

  .second-tip-block {
    margin-top: 0;
  }

  .third-tip-block {
    margin-top: 0;
  }

  .tips ul li {
    margin-top: 0;
    margin-bottom: 14px;
  }

  .third-tip-block {
    padding-left: 22px;
    padding-right: 13px;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;

    p {
      font-family: 'Open Sans Semibold', sans-serif;
      font-weight: 600;
      margin-bottom: 0;
    }

    p:nth-child(2) {
      margin-left: auto;
    }
  }

  .third-tip-block p:last-child {
    margin-top: 14px;
    flex: 0 1 100%;
  }

  .reviews-section {
    margin-top: 40px;
  }

  .reviews-section-header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 16px;
  }

  .rating-wrapper {
    margin-top: 19px;
    margin-left: 10px;
    flex-direction: row-reverse;
  }

  .average-review-rating {
    margin-left: 12px;
  }

  .first-tip-block,
  .second-tip-block {
    justify-content: flex-start;
    margin-left: 12px;
  }

  .news-section-title {
    margin-top: 40px;
    margin-bottom: 15px;
  }

  .benefits-title,
  .news-section-title,
  .offers-section-title {
    margin-left: 8px;
    margin-right: 8px;
  }

  .news-section,
  .benefits-section,
  .offers-section {
    .container {
      padding: 0;
    }
  }

  .author {
    font-size: 9px;
    margin-top: -20px;
  }

  .header-slider,
  .slide {
    min-height: 244px;
    height: 244px;
  }

  .header-content {
    top: 70px;
  }

  .title-block {
    width: 100%;
    min-height: 100px;
    height: auto;
    padding: 8px 13px;
    margin-top: 35px;

    .header-title {
      font-size: 26px;
      line-height: 34px;
      margin: 10px 8px 6px 0;
    }

    .header-subtitle {
      font-size: 16px;
      line-height: 21px;
      margin: 0;
    }
  }

  ::v-deep .header-pagination {
    transform: scale(0.9);
    margin-bottom: 3px;
  }
}
</style>
